import { UTCDate } from '@date-fns/utc';
import { zodResolver } from '@hookform/resolvers/zod';
import { Currency, LegalEntityCountry } from '@seapoint/server/enums';
import { addMonths, format } from 'date-fns';
import { Keyboard } from 'lucide-react';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../../design-system/button/button';
import { FormSelectField } from '../../../../design-system/form-select-field/form-select-field';
import { FormTextField } from '../../../../design-system/form-text-field/form-text-field';
import { Inline } from '../../../../design-system/inline/inline';
import { Select } from '../../../../design-system/select/select';
import { Stack } from '../../../../design-system/stack/stack';
import { supportedCountryCurrencies } from '../../../../types/supported-country-currency';
import { getCountryFlag } from '../../../../utils/get-country-flag';
import { getCurrencyDetails } from '../../../../utils/get-currency-details';
import { createPayrollSetupFormSchema } from './payroll-setup-form-validation-schema';

const COUNTRY_DISPLAY_NAMES = {
  GB: 'United Kingdom',
  IE: 'Ireland',
  DE: 'Germany',
} as const;

const COUNTRY_CURRENCY_MAP = {
  GB: 'GBP',
  IE: 'EUR',
  DE: 'EUR',
} as const;

type CountryCode = keyof typeof COUNTRY_DISPLAY_NAMES;

const generatePayrollMonths = () => {
  const MONTHS_TO_SHOW = 6;
  const months = [];
  const startDate = addMonths(new UTCDate(), -2);
  for (let i = 0; i < MONTHS_TO_SHOW; i++) {
    const date = addMonths(startDate, i);
    const value = format(date, 'MMMM yyyy');
    months.push({ key: i, value, label: value });
  }
  return months;
};

type PayrollSetupFormValues = z.infer<ReturnType<typeof createPayrollSetupFormSchema>>;

type PayrollSetupFormProps = {
  defaultValues?: Partial<PayrollSetupFormValues>;
  legalEntityCountry: LegalEntityCountry;
  onClick: (data: PayrollSetupFormValues) => void;
  employeesLength: number;
  isLoading?: boolean;
};

export const PayrollSetupForm = ({
  defaultValues = {},
  legalEntityCountry,
  employeesLength,
  onClick,
  isLoading = false,
}: PayrollSetupFormProps) => {
  const defaultPayrollTimePeriod = generatePayrollMonths()[2].value;
  const defaultPayrollReference = format(new Date(defaultPayrollTimePeriod), 'MMM yyyy');
  // TODO: We only support the legal entity country currencies for now.
  const filteredCurrencyCodes = supportedCountryCurrencies.filter(
    (currencyCode) => currencyCode === COUNTRY_CURRENCY_MAP[legalEntityCountry],
  );

  const currencySelectOptions = filteredCurrencyCodes.map((currencyCode) => {
    const { flag, name } = getCurrencyDetails(currencyCode);
    const currencyLabel = `${name} (${currencyCode})`;
    return { key: currencyCode, value: currencyCode, label: currencyLabel, flag };
  });

  const getCountryName = () => {
    return COUNTRY_DISPLAY_NAMES[legalEntityCountry];
  };

  const [currentCurrency, setCurrentCurrency] = React.useState<Currency | undefined>(undefined);
  const [hasAutoFilled, setHasAutoFilled] = React.useState(false);

  const form = useForm({
    mode: 'onSubmit',
    resolver: zodResolver(createPayrollSetupFormSchema(currentCurrency)),
    defaultValues: {
      country: legalEntityCountry ?? '',
      currency: defaultValues.currency ?? COUNTRY_CURRENCY_MAP[legalEntityCountry],
      payrollName: defaultValues.payrollName ?? '',
      payrollTimePeriod: defaultValues.payrollTimePeriod ?? defaultPayrollTimePeriod,
      payrollReference: defaultValues.payrollReference ?? defaultPayrollReference,
    },
    disabled: isLoading,
  });
  const selectedCountry = form.watch('country');
  const selectedCurrency = form.watch('currency');

  // Update resolver when currency changes to re-validate payrollReference
  React.useEffect(() => {
    if (selectedCurrency && selectedCurrency !== currentCurrency) {
      setCurrentCurrency(selectedCurrency as Currency);
      form.clearErrors();
      // Re-validate payrollReference field when currency changes
      form.trigger('payrollReference');
    }
  }, [selectedCurrency, currentCurrency, form]);

  // Auto-fill payroll name when country is selected
  React.useEffect(() => {
    if (selectedCountry && !form.getValues('payrollName') && !defaultValues.payrollName && !hasAutoFilled) {
      const countryName = COUNTRY_DISPLAY_NAMES[selectedCountry as CountryCode] || selectedCountry;
      form.setValue('country', selectedCountry);
      form.setValue('payrollName', `${countryName} Payroll`);
      form.setValue('payrollReference', `${defaultPayrollReference} Payroll`);
      form.setValue('payrollTimePeriod', defaultPayrollTimePeriod);
      setHasAutoFilled(true);
    }
  }, [selectedCountry, form, defaultPayrollTimePeriod, defaultValues, hasAutoFilled]);

  // Auto-select currency based on country
  React.useEffect(() => {
    // If we have default values (existing payroll), don't auto-select currency
    if (defaultValues.currency) {
      return;
    }

    if (selectedCountry && !selectedCurrency) {
      const defaultCurrency = COUNTRY_CURRENCY_MAP[selectedCountry as CountryCode];
      if (defaultCurrency) {
        form.setValue('currency', defaultCurrency);
      }
    }
  }, [selectedCountry, selectedCurrency, form, defaultValues]);

  const handleSubmit = (data: PayrollSetupFormValues) => {
    onClick(data);
  };

  return (
    <div className="rounded-lg bg-white">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <Stack space={10} className="w-full p-6">
            <Stack space={4} align="center" className="w-full">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-seapoint-gray-background-modal">
                <Keyboard className="h-7 w-7" strokeWidth={1} />
              </div>
              <Stack space={1} className="w-full" align="center">
                <h1 className="text-seapoint-gray-900 text-center text-2xl">Payroll setup</h1>

                <p className="text-center text-seapoint-gray-text">
                  Begin by selecting the country you are making payroll for
                </p>
              </Stack>
            </Stack>

            <Stack space={4} className="w-full">
              <FormSelectField
                label="Country"
                inputName="country"
                placeholder="Select country"
                triggerClassName="text-base"
              >
                <Select.Item key={legalEntityCountry} value={legalEntityCountry} className="py-3">
                  <Inline space={3} justify="between" className="w-full">
                    <Inline space={3}>
                      <img
                        src={getCountryFlag(legalEntityCountry)}
                        alt={getCountryName()}
                        className="h-6 w-6 rounded-full object-cover"
                      />
                      <p className="text-base font-medium text-black">{getCountryName()}</p>
                    </Inline>

                    {employeesLength > 0 && (
                      <Inline space={2}>
                        <span className="text-sm text-seapoint-gray-text">{employeesLength} Employees</span>
                      </Inline>
                    )}
                  </Inline>
                </Select.Item>
              </FormSelectField>

              <FormSelectField
                label="Currency"
                inputName="currency"
                placeholder="Select currency"
                triggerClassName="text-base"
              >
                {currencySelectOptions.map(({ key, value, label, flag }) => {
                  return (
                    <Select.Item key={key} value={value} className="py-3">
                      <Inline space={3}>
                        <div className="h-6 w-6 overflow-clip rounded-full">
                          <img src={flag} alt="" loading="eager" fetchPriority="high" className="h-full w-full" />
                        </div>
                        <p className="text-base font-medium text-black">{label}</p>
                      </Inline>
                    </Select.Item>
                  );
                })}
              </FormSelectField>
              <Inline space={4} className="w-full justify-between" wrap="nowrap" align="start">
                <div className="w-1/2">
                  <FormTextField label="Payroll Name" inputName="payrollName" placeholder="Enter payroll name" />
                </div>
                <div className="w-1/2">
                  <FormSelectField
                    label="Payroll Time Period"
                    inputName="payrollTimePeriod"
                    placeholder="Select payroll time period"
                    triggerClassName="text-base w-full"
                  >
                    {generatePayrollMonths().map(({ key, value, label }) => (
                      <Select.Item key={key} value={value} className="py-3">
                        <p className="text-base font-medium text-black">{label}</p>
                      </Select.Item>
                    ))}
                  </FormSelectField>
                </div>
              </Inline>
              <div className="w-full">
                <FormTextField
                  label="Payroll Reference"
                  inputName="payrollReference"
                  placeholder="Enter payroll reference"
                />
              </div>
            </Stack>

            <Button
              type="submit"
              size="lg"
              className="w-full bg-seapoint-yellow font-medium text-black hover:bg-seapoint-yellow/80"
              disabled={isLoading}
              isLoading={isLoading}
            >
              Continue
            </Button>
          </Stack>
        </form>
      </FormProvider>
    </div>
  );
};
